<template>
  <MainLayout>
    <v-layout style="padding: 1em" justify-center row fill-height>
      <v-flex>
        <v-card style="height: 100%" light color="#f4f4fa">
          <v-layout column style="height: 100%">
            <!-- Title -->
            <v-card-title row class="align-center">
              <span class="headline flex-none">Citizens</span>
            </v-card-title>

            <!-- Filter -->
            <div class="d-flex flex-xs-column justify-end align-center">
              <!-- Search Field -->
              <v-text-field
                class="mx-4"
                prepend-icon="search"
                v-model="filters.search"
                @input="getUsers"
                label="Search by mobile, name"
                clearable
              />

              <!-- Filter Date Range -->
              <div class="d-flex flex-none mx-4">
                <DatePicker
                  v-model="filters.fromDate"
                  clearable
                  label="From Date"
                  @change="getUsers"
                />

                <DatePicker
                  v-model="filters.toDate"
                  clearable
                  label="To Date"
                  @change="getUsers"
                />
              </div>
            </div>

            <!-- Table -->
            <v-container fluid fill-height class="full-height">
              <v-layout style="padding: 0" class="full-height">
                <v-flex sm12 md12 class="full-height scroll">
                  <v-data-table
                    :headers="tableHeaders"
                    :items="items"
                    :pagination.sync="pagination"
                    :rows-per-page-items="[10, 50, 100]"
                    :total-items="pagination.totalItems"
                    :loading="loading"
                    @update:pagination="getUsers"
                    class="elevation-1"
                  >
                    <!-- When items = [] -->
                    <template v-slot:no-data>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment"
                            : "No matching records found"
                        }}
                      </div>
                    </template>

                    <!-- When items = undefined -->
                    <template v-slot:no-results>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment"
                            : "Data will be displayed in here"
                        }}
                      </div>
                    </template>

                    <template v-slot:items="{ item }">
                      <td>
                        <template>
                          <img
                            v-if="!item.avatar"
                            class="avatar"
                            src="@/assets/images/avatar.png"
                          />
                          <img v-else class="avatar" :src="item.avatar" />
                        </template>
                      </td>
                      <td>{{ item.name }}</td>
                      <td>{{ item.email }}</td>
                      <td>{{ item.mobile }}</td>
                      <td>{{ item.total_issues }}</td>
                      <td class="notification-content">
                        <v-badge color="#4990e2">
                          <template
                            v-if="
                              item.suggestions.filter(
                                x => x.user_id === x.created_by
                              ).length > 0
                            "
                            v-slot:badge
                          >
                            <span>{{
                              item.suggestions.filter(
                                x => x.user_id === x.created_by
                              ).length
                            }}</span>
                          </template>
                          <v-btn
                            title="View Suggestions"
                            @click="showSuggestion(item)"
                            icon
                          >
                            <v-icon large small>sms</v-icon>
                          </v-btn>
                        </v-badge>
                      </td>
                      <td>{{ item.created_at | dateTime }}</td>

                      <td v-if="isAdmin">
                        <!-- Active/Deactive Citizen -->
                        <v-btn
                          v-if="item.confirmed"
                          icon
                          title="Deactive"
                          @click="deactiveCitizen(item)"
                        >
                          <v-icon color="error">block</v-icon>
                        </v-btn>

                        <v-btn
                          v-else
                          icon
                          title="Active"
                          @click="activeCitizen(item)"
                        >
                          <v-icon color="success">check_circle_outline</v-icon>
                        </v-btn>

                        <!-- Delete -->
                        <v-btn icon title="Delete" @click="deleteCitizen(item)">
                          <v-icon color="error">delete</v-icon>
                        </v-btn>
                      </td>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-layout>
            </v-container>
          </v-layout>
        </v-card>
      </v-flex>

      <!-- Suggestion Dialog -->
      <v-dialog light width="37.5em" v-model="showSuggestionDialog">
        <v-flex light>
          <v-card light>
            <v-layout
              class="elevation-1"
              v-if="selectedItem.id"
              style="width: 100%; height: 100%; overflow-y: hidden; padding: 1.25em"
              column
            >
              <!-- Title -->
              <span
                class="title d-flex align-center"
                style="margin-bottom: 1.25em; float:left"
              >
                <img
                  v-if="!selectedItem.avatar"
                  class="avatar mr-1 flex-none"
                  src="@/assets/images/avatar.png"
                />
                <img
                  v-else
                  class="avatar mr-1 flex-none"
                  :src="selectedItem.avatar"
                />

                <div class="flex">{{ selectedItem.name }}</div>
              </span>

              <!-- Close Dialog -->
              <v-btn
                style="right: 0;top: 0;position: absolute;"
                icon
                @click="showSuggestionDialog = false"
              >
                <v-icon>close</v-icon>
              </v-btn>

              <!-- Timeline -->
              <div
                ref="timelineContainer"
                style="padding: 0.625em; width: 100%; max-height: 60vh; overflow-y: auto"
              >
                <v-timeline
                  style="width:100%"
                  v-if="selectedItem.suggestions.length > 0"
                >
                  <v-timeline-item
                    v-for="(item, idx) of selectedItem.suggestions"
                    :key="idx"
                    :left="!(item.created_by !== selectedItem.id)"
                    :right="item.created_by !== selectedItem.id"
                  >
                    <template v-slot:opposite>
                      <label class="body-2">
                        {{ item.user_created.name }}
                      </label>
                      <div class="caption">
                        {{ convertDateTime(item.created_at) }}
                      </div>
                    </template>

                    <v-card class="elevation-1">
                      <v-spacer />

                      <v-menu offset-y>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            style="margin-right:0; float:right; margin-top: 0"
                            title="Options"
                            small
                            icon
                            v-on="on"
                          >
                            <v-icon small>more_vert</v-icon>
                          </v-btn>
                        </template>

                        <v-list>
                          <v-list-tile @click="deleteComment(item, idx)">
                            <v-list-tile-title style="color: red"
                              >Remove</v-list-tile-title
                            >
                          </v-list-tile>
                        </v-list>
                      </v-menu>

                      <v-card-text>{{ item.message }}</v-card-text>

                      <v-progress-linear
                        v-if="deletingComment && currentComment === item"
                        indeterminate
                        :height="3"
                        style="margin: 0"
                      />
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
                <div class="title py-4" v-else>
                  No Comments yet!
                </div>
              </div>

              <!-- Comment Text Field -->
              <v-text-field
                v-if="selectedItem.id"
                ref="commentTextField"
                v-model="commentText"
                autofocus
                hide-details
                solo
                label="Leave a comment"
                :loading="addingComment"
                :disabled="addingComment"
                @keyup.enter="comment"
              >
                <template v-slot:append>
                  <v-btn
                    class="mx-0"
                    depressed
                    icon
                    :disabled="addingComment"
                    @click="comment"
                  >
                    <v-icon>send</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-layout>
          </v-card>
        </v-flex>
      </v-dialog>
    </v-layout>
  </MainLayout>
</template>

<script>
import moment from "moment";
import api from "@/api";
import { PLATFORMS } from "@/constants/platforms";

import DatePicker from "@/components/DatePicker";

import { debounce } from "@/utils/Debounce";
import sdk from "@/api/sdk";

export default {
  metaInfo: {
    title: "Citizens",
    meta: [{ name: "Citizens", content: "citizens management" }]
  },

  components: {
    DatePicker
  },

  data() {
    return {
      showDialog: false,
      showSuggestionDialog: false,

      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sortBy: null,
        totalItems: 0
      },

      filters: {
        search: undefined,
        fromDate: undefined,
        toDate: undefined
      },

      loading: false,
      items: undefined,
      selectedItem: {},
      headers: [
        {
          text: "",
          width: 60,
          sortable: false
        },
        {
          text: "Name",
          value: "name",
          sortable: false
        },
        {
          text: "Email",
          value: "email",
          sortable: false
        },
        {
          text: "Mobile",
          width: 320,
          sortable: false
        },
        {
          text: "Total Issues",
          width: 60,
          sortable: false
        },
        {
          text: "Suggestions",
          width: 60,
          sortable: false
        },
        {
          text: "Created At",
          value: "created_at",
          width: 380,
          sortable: false
        }
      ],

      addingComment: false,
      deletingComment: false,
      commentText: "",
      currentComment: undefined
    };
  },

  computed: {
    isAdmin() {
      return (
        this.$store.state.auth.currentUser.platform === PLATFORMS.ADMINISTRATION
      );
    },

    tableHeaders() {
      return this.isAdmin
        ? this.headers.concat([
            {
              text: "Actions",
              sortable: false
            }
          ])
        : this.headers;
    }
  },

  watch: {
    showSuggestionDialog(visible) {
      if (visible) {
        this.$nextTick(() => {
          this.scrollTimeLineToBottom();
        });
      }
    }
  },

  mounted() {
    this.getUsers();
  },

  methods: {
    convertTime(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleTimeString();
    },

    convertDate(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleDateString();
    },

    convertDateTime(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleString();
    },

    showSuggestion(item) {
      this.showSuggestionDialog = true;
      this.commentText = "";
      this.selectedItem = {};
      this.selectedItem = item;
    },

    async comment() {
      try {
        this.addingComment = true;

        const res = await api.addCommentSg({
          user_id: this.selectedItem.id,
          message: this.commentText
        });

        this.commentText = "";
        this.selectedItem.suggestions.push(res.data.suggestion);

        this.$nextTick(() => {
          this.$refs.commentTextField.focus();
          this.scrollTimeLineToBottom();
        });
      } catch (error) {
        this.$notify.error("An error, please retry!");
      } finally {
        this.addingComment = false;
      }
    },

    async deleteComment(item, index) {
      try {
        this.deletingComment = true;
        this.currentComment = item;

        await api.deleteCommentSg(item.id);

        this.selectedItem.suggestions.splice(
          this.selectedItem.suggestions.length - index - 1,
          1
        );
      } catch (error) {
        this.$notify.error("An error, please retry!");
      } finally {
        this.deletingComment = false;
      }
    },

    getUsers: debounce(async function() {
      try {
        this.loading = true;
        this.items = undefined;

        const res = await api.getCitizenUsers({
          page_size: this.pagination.rowsPerPage,
          page: this.pagination.page,
          search: this.filters.search,
          start_time: this.filters.fromDate
            ? moment(this.filters.fromDate)
                .startOf("day")
                .format()
            : undefined,
          end_time: this.filters.toDate
            ? moment(this.filters.toDate)
                .endOf("day")
                .format()
            : undefined
        });

        this.loading = false;
        this.items = res.data.data;
        this.pagination.totalItems = res.data.total;
      } finally {
        this.loading = false;
      }
    }, 500),

    async deleteCitizen(user) {
      try {
        await this.$confirm(
          "All relation data of user will lose. Continue?",
          "Warning",
          {
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            type: "warning"
          }
        );
      } catch (error) {
        return;
      }

      try {
        this.loading = true;
        this.items = undefined;

        const res = await api.deleteUser(user.id);

        this.$notify.success(res.data.message);

        this.getUsers();
      } catch (error) {
        this.$notify.error(error.message);
      } finally {
        this.loading = false;
      }
    },

    async activeCitizen(user) {
      try {
        await this.$confirm("Do you want to active this citizen?", "Warning", {
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          type: "warning"
        });
      } catch (error) {
        return;
      }

      try {
        this.loading = true;

        const res = await sdk.request({
          method: "POST",
          url: `/citizens/${user.id}/active`
        });

        this.$notify.success(res.data.message);

        user.confirmed = true;
      } catch (error) {
        this.$notify.error(error.message);
      } finally {
        this.loading = false;
      }
    },

    async deactiveCitizen(user) {
      try {
        await this.$confirm(
          "Do you want to deactive this citizen?",
          "Warning",
          {
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            type: "warning"
          }
        );
      } catch (error) {
        return;
      }

      try {
        this.loading = true;

        const res = await sdk.request({
          method: "POST",
          url: `/citizens/${user.id}/deactive`
        });

        this.$notify.success(res.data.message);

        user.confirmed = false;
      } catch (error) {
        this.$notify.error(error.message);
      } finally {
        this.loading = false;
      }
    },

    scrollTimeLineToBottom() {
      this.$refs.timelineContainer.scrollTop = this.$refs.timelineContainer.scrollHeight;
    }
  }
};
</script>

<style lang="css" scoped>
.avatar {
  height: 2.25em;
  width: 2.25em;
  border-radius: 50%;
}
.full-height {
  flex: 1 1 auto;
  min-height: 0;
}
.scroll {
  overflow-y: auto;
}
</style>
